<template>
  <div>
    <Header />

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Manage Business Designations</h5>
        <p class="page-sub">Create and manage your organisation's positions</p>
      </div>
      <div class="page-actions">

        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input type="text" class="search-input" placeholder="Search positions..."
            v-model="tableData.search" @input="debouncedFetch" />
        </div>

        <select v-model="statusFilter" @change="tableData.page = 1; getDetails()" class="filter-select">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <button class="btn-danger-outline"
          :disabled="!canBulkDelete"
          data-bs-toggle="modal" data-bs-target="#bulkModal">
          <font-awesome-icon icon="trash" />
          Bulk Delete
          <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
        </button>

        <button class="btn-primary-action" :disabled="permission.create_position != 1" @click="openCreate">
          <font-awesome-icon icon="plus" /> Create Position
        </button>

      </div>
    </div>

    <!-- ── Data card ── -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading positions...</p>
      </div>

      <!-- 403 -->
      <div v-else-if="permission_status === 403" class="state-block">
        <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
        <p class="state-title">Access Denied</p>
        <p class="state-sub">You don't have permission to view positions.</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon state-icon--blue"><font-awesome-icon icon="tags" /></div>
        <p class="state-title">No positions found</p>
        <p class="state-sub">
          {{ tableData.search ? 'No results match your search.' : 'Create your first department to get started.' }}
        </p>
        <button v-if="!tableData.search" class="btn-primary-action mt-3" :disabled="permission.create_position != 1" @click="openCreate">
          <font-awesome-icon icon="plus" /> Create Position
        </button>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            <strong>{{ details.length }}</strong> position{{ details.length !== 1 ? 's' : '' }}
            <span v-if="selectedModel.length > 0" class="selected-hint">
              · {{ selectedModel.length }} selected
            </span>
          </span>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                </th>
                <th>Name</th>
                <th>Department</th>
                <th>Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(position, index) in details" :key="position.id"
                :class="{ 'row--selected': selectedModel.includes(position.id) }">

                <td class="col-check">
                  <input type="checkbox" class="row-check" v-model="selectedModel" :value="position.id" />
                </td>

                <td>
                  <div class="category-cell">
                    <div class="category-icon"><font-awesome-icon icon="tag" /></div>
                    <span class="category-name">{{ position.name }}</span>
                  </div>
                </td>

                <td class="text-muted-cell">
                  {{ position.department
                    ? position.department.name : '—' }}
                </td>

                <td>
                  <label class="toggle-switch">
                    <input type="checkbox"
                      :true-value="1" :false-value="0"
                      v-model="statuses[index]"
                      :disabled="permission.update_position != 1"
                      @change="updateStatus(position.id)" />
                    <span class="toggle-track"><span class="toggle-thumb"></span></span>
                    <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                      {{ statuses[index] ? 'Active' : 'Inactive' }}
                    </span>
                  </label>
                </td>

                <td class="col-actions">
                  <div class="actions-cell">
                    <button class="action-btn action-btn--edit" title="Edit"
                      :disabled="permission.update_position != 1"
                      @click="openEdit(position)">
                      <font-awesome-icon icon="pen" />
                    </button>
                    <button class="action-btn action-btn--delete" title="Delete"
                      :disabled="permission.delete_position != 1"
                      @click="openDelete(position)">
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

      </template>
    </div>


    <!-- ══════════════════════════════
         CREATE MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
      <div class="modal-box">
        <div class="modal-hd">
          <span class="modal-ttl">Create Business Position</span>
          <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="createData">
          <div class="modal-bd">
            <div class="field-group">
              <label class="field-label"> Name <span class="req">*</span></label>
              <input ref="createInput" v-model="name" type="text" class="field-input"
                placeholder="e.g. Accountant" required />
            </div>

            <div class="field-group">
              <label class="field-label">Department</label>
              <select v-model="department_id" class="field-input field-select">
                <option value="">— Select department —</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </option>
              </select>
            </div>

            <div class="field-group">
              <label class="field-label">Description </label>
              <textarea v-model="description" class="field-input"></textarea>
            </div>
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!name.trim() || crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="plus" class="me-1" />
              {{ crudLoading ? 'Creating...' : 'Create Position' }}
            </button>
          </div>
        </form>
      </div>
    </div>


    <!-- ══════════════════════════════
         EDIT MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
      <div class="modal-box">
        <div class="modal-hd">
          <span class="modal-ttl">Edit Business Position</span>
          <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="updateData">
          <div class="modal-bd">
            <div class="field-group">
              <label class="field-label"> Name <span class="req">*</span></label>
              <input v-model="name" type="text" class="field-input"
                placeholder="" required />
            </div>

            <div class="field-group">
              <label class="field-label">Department <span class="req">*</span></label>
              <select v-model="department_id" class="field-input field-select" required>
                <option value="">— Select department —</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </option>
              </select>
            </div>

            <div class="field-group">
              <label class="field-label">Description </label>
              <textarea v-model="description" class="field-input"></textarea>
            </div>
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="!name.trim() || crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="save" class="me-1" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="createOpen || editOpen" class="modal-backdrop-custom" />

    <!-- ── Delete modals ── -->
    <SingleDeleteModal
      :modalId="'singleModal'"
      @deleteSingle="callSingleDeleteButton"
      :modalText="'Are you sure you want to delete this position?'"
      :action="'deleteSingle'"
    />
    <BulkDeleteModal
      :modalId="'bulkModal'"
      @deleteBulk="callBulkDeleteButton"
      :modalText="'Are you sure you want to delete the selected positions?'"
    />

  </div>
</template>


<script>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import Header            from './component/header.vue';
import SingleDeleteModal from '../../../../components/ActionModal.vue';
import BulkDeleteModal   from '../../../../components/bulkDeleteModal.vue';

export default {
  name: 'Departments',

  components: { Header, SingleDeleteModal, BulkDeleteModal },

  setup() {
    const store = useStore();
    const toast = useToast();

    // ── State ────────────────────────────────────────────────────────
    const details          = ref([]);
    const departments          = ref([]);
    const statuses         = ref([]);
    const loading          = ref(false);
    const crudLoading      = ref(false);
    const permission_status = ref(null);
    const name             = ref('');
    const description             = ref('');
    const department_id    = ref('');
    const position_id    = ref('');
    const selectedModel    = ref([]);
    const allSelected      = ref(false);
    const createOpen       = ref(false);
    const editOpen         = ref(false);
    const createInput      = ref(null);

    // ── tableData — single object for all filter/search params ───────
    const tableData = ref({
      search: '',
      length: 100,
    });

    // String model for the select — avoids Vue array-by-reference comparison issues
    const statusFilter = ref('all');

    // Convert string key → array the backend expects
    const statusMap = { all: [0, 1], active: [1], inactive: [0] };

    const permission    = computed(() => store.state.getCurrentUserPermission);
    const canBulkDelete = computed(() =>
      permission.value?.delete_position == 1 && selectedModel.value.length > 0,
    );

    // ── Selection ────────────────────────────────────────────────────
    const selectAll = () => {
      selectedModel.value = allSelected.value ? details.value.map(d => d.id) : [];
    };

    // ── Modal helpers ─────────────────────────────────────────────────
    const openCreate = async () => {
      name.value        = '';
      description.value        = '';
      department_id.value = '';
      createOpen.value  = true;
      await nextTick();
      createInput.value?.focus();
    };
    const closeCreate = () => {
      createOpen.value  = false;
      name.value        = '';
      description.value        = '';
      department_id.value = '';
    };

    const openEdit = (position) => {
      position_id.value = position.id;
      name.value          = position.name;
      description.value          = position.description ?? '';
      department_id.value   = position.department?.id ?? position.department_id ?? '';
      editOpen.value      = true;
    };
    const closeEdit = () => {
      editOpen.value      = false;
      name.value          = '';
      description.value          = '';
      department_id.value = '';
      position_id.value = '';
    };

    const openDelete = (position) => {
      position_id.value = position.id;
      name.value          = position.name;
      window.$('#singleModal').modal('show');
    };

    // ── Data fetching ─────────────────────────────────────────────────
    const getDetails = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get('payroll/position', {
          params: { ...tableData.value, status: statusMap[statusFilter.value] },
        });
        // Paginated Laravel response: { data: { data: [...], total: N, ... } }
        const payload = Array.isArray(data.data?.data)
          ? data.data.data
          : Array.isArray(data.data)
            ? data.data
            : [];
        details.value  = payload;
        statuses.value = details.value.map(d => d.is_active);
        // Deselect any rows that no longer exist after refresh
        const ids = new Set(details.value.map(d => d.id));
        selectedModel.value = selectedModel.value.filter(id => ids.has(id));
        if (!selectedModel.value.length) allSelected.value = false;
      } catch (err) {
        permission_status.value = err?.response?.status ?? null;
        if (err?.response?.status !== 403) {
          toast.error(err.response?.data?.message ?? 'Could not load positions.');
        }
      } finally {
        loading.value = false;
      }
    };

    const getDepartments = async () => {
      try {
        const { data } = await axios.get('payroll/department/active');
        departments.value = data.data;
      } catch (_) { /* intentionally silent */ }
    };

    // ── CRUD ──────────────────────────────────────────────────────────
    const createData = async () => {
      if (!name.value.trim()) return;
      crudLoading.value = true;
      try {
        await axios.post('/payroll/position', {
          name:        name.value,
          description:        description.value,
          department_id: department_id.value,
        });
        toast.success('Position created successfully.');
        closeCreate();
        getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) toast.warning(flatError(err.response.data.message));
        else if (status === 403) toast.error(err.response.data.message);
        else toast.error('An error occurred. Please try again.');
      } finally {
        crudLoading.value = false;
      }
    };

    const updateData = async () => {
      if (!name.value.trim()) return;
      crudLoading.value = true;
      try {
        await axios.put(`/payroll/position/${position_id.value}`, {
          name:        name.value,
          description:        description.value,
          department_id: department_id.value,
        });
        toast.success('Position updated successfully.');
        closeEdit();
        getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) toast.warning(flatError(err.response.data.message));
        else if (status === 403) toast.error(err.response.data.message);
        else toast.error('An error occurred. Please try again.');
      } finally {
        crudLoading.value = false;
      }
    };

    const updateStatus = async (id) => {
      try {
        const { data } = await axios.put('payroll/position/update-status/' + id);
        toast.success(data.message);
        getDetails();
      } catch (_) {
        toast.error('Could not update status.');
        getDetails(); // revert local toggle on failure
      }
    };

    const callSingleDeleteButton = async () => {
      try {
        await axios.delete(`/payroll/position/${position_id.value}`);
        toast.success('position deleted successfully.');
        position_id.value = '';
        name.value          = '';
        getDetails();
      } catch (err) {
        toast.error(err.response?.data?.message ?? 'Could not delete department.');
      }
    };

    const callBulkDeleteButton = async () => {
      try {
        const { data } = await axios.post('/payroll/position/delete-bulk', {
          positions: selectedModel.value,
        });
        toast.success(data.message);
        selectedModel.value = [];
        allSelected.value   = false;
        getDetails();
      } catch (err) {
        const status = err.response?.status;
        if (status === 422) toast.error(flatError(err.response.data.message));
        else toast.error(err.response.data?.message ?? 'Bulk delete failed.');
      }
    };

    // ── Helpers ───────────────────────────────────────────────────────
    const flatError = (msg) => {
      if (typeof msg === 'string') return msg;
      if (Array.isArray(msg)) return msg[0];
      if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? 'Validation error.';
      return 'An error occurred.';
    };

    // ── Debounced search on tableData.search ──────────────────────────
    let searchTimer = null;
    const debouncedFetch = () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => getDetails(), 300);
    };

    onMounted(() => {
      getDetails();
      getDepartments();
    });

    return {
      details, departments, statuses, loading, crudLoading, permission_status,
      tableData, name, description, department_id,
      selectedModel, allSelected, selectAll,
      createOpen, editOpen, createInput,
      openCreate, closeCreate, openEdit, closeEdit, openDelete,
      createData, updateData, updateStatus,
      callSingleDeleteButton, callBulkDeleteButton,
      getDetails, debouncedFetch, statusFilter, getDepartments,
      permission, canBulkDelete,
    };
  },
};
</script>


<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Search ──────────────────────────────────────────────────────────── */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.45; cursor:not-allowed; }
.btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; position:relative; font-family:inherit; }
.btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
.btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
.bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; margin-left:2px; }
.btn-ghost  { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.mt-3 { margin-top:12px; }
.me-1 { margin-right:4px; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
.state-icon--red  { background:#fff0f0; color:#e03535; }
.state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub        { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell  { color:#4a5e78; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar { display:flex; align-items:center; padding:14px 20px; border-bottom:1px solid #e4eaf2; }
.table-count   { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.selected-hint { color:#004AAD; font-weight:600; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr.row--selected { background:#edf4ff; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.col-check   { width:44px; padding-left:20px !important; }
.col-actions { width:90px; text-align:center; }
.row-check   { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }

/* ── Cells ───────────────────────────────────────────────────────────── */
.category-cell { display:flex; align-items:center; gap:10px; }
.category-icon { width:30px; height:30px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
.category-name { font-size:13px; font-weight:500; color:#1a2b45; }
.code-badge    { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }

/* ── Toggle switch ───────────────────────────────────────────────────── */
.toggle-switch  { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
.toggle-switch input { display:none; }
.toggle-track   { width:36px; height:20px; border-radius:10px; background:#dde5f0; position:relative; transition:background .2s; flex-shrink:0; }
.toggle-thumb   { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
.toggle-switch input:checked ~ .toggle-track { background:#004AAD; }
.toggle-switch input:checked ~ .toggle-track .toggle-thumb { transform:translateX(16px); }
.toggle-switch input:disabled ~ .toggle-track { opacity:.5; cursor:not-allowed; }
.toggle-label     { font-size:12px; font-weight:600; }
.toggle-label.on  { color:#16a34a; }
.toggle-label.off { color:#94a3b8; }

/* ── Action buttons ──────────────────────────────────────────────────── */
.actions-cell    { display:flex; align-items:center; justify-content:center; gap:6px; }
.action-btn      { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
.action-btn--edit:hover:not(:disabled)   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
.action-btn--delete:hover:not(:disabled) { background:#fff0f0; color:#e03535; border-color:#fecaca; }
.action-btn:disabled { opacity:.35; cursor:not-allowed; }

/* ── Modal ───────────────────────────────────────────────────────────── */
.modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
.modal-box             { background:#fff; border-radius:14px; width:100%; max-width:460px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-hd  { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
.modal-ttl { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-bd  { padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
.modal-ft  { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.field-group  { display:flex; flex-direction:column; gap:5px; }
.field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
.req          { color:#e03535; }
.field-input  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-select { appearance:auto; cursor:pointer; }
.field-hint   { font-size:11.5px; color:#7a90b0; margin-top:2px; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
}
</style>